/**
 * Appointment Management Hooks
 * TanStack Query hooks for appointment CRUD operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  DentalOfficeAppointment,
  AppointmentStatus,
  ApiResponse,
} from '../types';

// Query keys
export const appointmentKeys = {
  all: ['appointments'] as const,
  lists: () => [...appointmentKeys.all, 'list'] as const,
  list: (filters: AppointmentFilters) => [...appointmentKeys.lists(), filters] as const,
  details: () => [...appointmentKeys.all, 'detail'] as const,
  detail: (id: number) => [...appointmentKeys.details(), id] as const,
  today: () => [...appointmentKeys.all, 'today'] as const,
  week: (date: string) => [...appointmentKeys.all, 'week', date] as const,
  availableSlots: (params: AvailableSlotParams) =>
    [...appointmentKeys.all, 'available-slots', params] as const,
};

// Types
export interface AppointmentFilters {
  patient_id?: number;
  provider_id?: number;
  status?: AppointmentStatus;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface CreateAppointmentInput {
  patient_id: number;
  provider_id?: number;
  appointment_datetime: string;
  duration_minutes: number;
  appointment_type: string;
  reason?: string;
  notes?: string;
  status?: AppointmentStatus;
}

export interface UpdateAppointmentInput {
  patient_id?: number;
  provider_id?: number;
  appointment_datetime?: string;
  duration_minutes?: number;
  appointment_type?: string;
  reason?: string;
  notes?: string;
  status?: AppointmentStatus;
}

export interface RescheduleAppointmentInput {
  appointment_datetime: string;
  duration_minutes?: number;
  reason?: string;
}

export interface AvailableSlotParams {
  provider_id?: number;
  date: string;
  duration_minutes: number;
  exclude_appointment_id?: number;
}

export interface AvailableSlot {
  start: string;
  end: string;
  available: boolean;
}

export interface AppointmentWithDetails extends DentalOfficeAppointment {
  patient_name?: string;
  provider_name?: string;
}

// ============================================================================
// LIST APPOINTMENTS
// ============================================================================

export function useAppointmentsQuery(filters: AppointmentFilters = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: appointmentKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.patient_id) params.set('patient_id', String(filters.patient_id));
      if (filters.provider_id) params.set('provider_id', String(filters.provider_id));
      if (filters.status) params.set('status', filters.status);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      params.set('limit', String(filters.limit || 100));
      params.set('offset', String(filters.offset || 0));

      const response = await api.get<
        ApiResponse<AppointmentWithDetails[]>
      >(`/appointments?${params.toString()}`, token || undefined);

      return response;
    },
    enabled: !!token,
  });
}

// ============================================================================
// TODAY'S APPOINTMENTS
// ============================================================================

export function useTodayAppointmentsQuery() {
  const { token } = useAuth();

  return useQuery({
    queryKey: appointmentKeys.today(),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<AppointmentWithDetails[]>
      >('/appointments/today', token || undefined);
      return response.data;
    },
    enabled: !!token,
    refetchInterval: 60000, // Refresh every minute
  });
}

// ============================================================================
// WEEK APPOINTMENTS
// ============================================================================

export function useWeekAppointmentsQuery(weekStart?: string) {
  const { token } = useAuth();

  return useQuery({
    queryKey: appointmentKeys.week(weekStart || 'current'),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (weekStart) params.set('week_start', weekStart);

      const response = await api.get<
        ApiResponse<AppointmentWithDetails[]>
      >(`/appointments/week?${params.toString()}`, token || undefined);
      return response.data;
    },
    enabled: !!token,
    refetchInterval: 60000, // Refresh every minute
  });
}

// ============================================================================
// GET SINGLE APPOINTMENT
// ============================================================================

export function useAppointmentQuery(id: number | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: appointmentKeys.detail(id!),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<AppointmentWithDetails>
      >(`/appointments/${id}`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!id,
  });
}

// ============================================================================
// AVAILABLE SLOTS
// ============================================================================

export function useAvailableSlotsQuery(params: AvailableSlotParams | null) {
  const { token } = useAuth();

  return useQuery({
    queryKey: appointmentKeys.availableSlots(params!),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.provider_id) queryParams.set('provider_id', String(params.provider_id));
      queryParams.set('date', params!.date);
      queryParams.set('duration_minutes', String(params!.duration_minutes));
      if (params?.exclude_appointment_id) {
        queryParams.set('exclude_appointment_id', String(params.exclude_appointment_id));
      }

      const response = await api.get<
        ApiResponse<AvailableSlot[]>
      >(`/appointments/available-slots?${queryParams.toString()}`, token || undefined);
      return response.data;
    },
    enabled: !!token && !!params,
  });
}

// ============================================================================
// CREATE APPOINTMENT
// ============================================================================

export function useCreateAppointment() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAppointmentInput) => {
      const response = await api.post<
        ApiResponse<DentalOfficeAppointment>
      >('/appointments', data, token || undefined);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all appointment lists
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.today() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

// ============================================================================
// UPDATE APPOINTMENT
// ============================================================================

export function useUpdateAppointment(id: number) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAppointmentInput) => {
      const response = await api.put<
        ApiResponse<DentalOfficeAppointment>
      >(`/appointments/${id}`, data, token || undefined);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate appointment lists and detail
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.today() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

// ============================================================================
// UPDATE APPOINTMENT STATUS
// ============================================================================

export function useUpdateAppointmentStatus(id: number) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (status: AppointmentStatus) => {
      const response = await api.post<
        ApiResponse<DentalOfficeAppointment>
      >(`/appointments/${id}/status`, { status }, token || undefined);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate appointment lists and detail
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.today() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

// ============================================================================
// RESCHEDULE APPOINTMENT
// ============================================================================

export function useRescheduleAppointment(id: number) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RescheduleAppointmentInput) => {
      const response = await api.post<
        ApiResponse<DentalOfficeAppointment>
      >(`/appointments/${id}/reschedule`, data, token || undefined);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate appointment lists and detail
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.today() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

// ============================================================================
// CANCEL APPOINTMENT
// ============================================================================

export function useCancelAppointment() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/appointments/${id}`, token || undefined);
    },
    onSuccess: () => {
      // Invalidate all appointment lists
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.today() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}
